import type {
  AnnotationInteraction,
  AnnotationTrigger,
  AnnotationStateRecord,
  WorldAnnotation,
} from '../types/worldAnnotation';
import {
  buildLegacyConditionGroup,
  normalizeAnnotationConditionGroup,
} from './worldAnnotationConditions';
import {
  cloneAnnotationInteractions,
  cloneAnnotationStateRecord,
  cloneAnnotationTriggers,
} from './worldAnnotationRuntimeClone';
import {
  AUTHORED_STATE_SYMBOL,
  MAX_INTERACTIONS,
  MAX_TRIGGER_COOLDOWN_MS,
  MAX_TRIGGERS,
  normalizeActionToken,
  normalizeAnnotationStateRecord,
  normalizeEventName,
  normalizeInteractionPayload,
  normalizeTriggerEffect,
  normalizeTriggerEvent,
  sanitizeToken,
  type AnnotationRuntimeStateById,
} from './worldAnnotationRuntimeNormalizers';

export const normalizeAnnotationInteractions = (
  value: unknown,
): AnnotationInteraction[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const byAction = new Map<string, AnnotationInteraction>();
  value.slice(0, MAX_INTERACTIONS).forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return;
    }
    const candidate = entry as Record<string, unknown>;
    const effectType =
      candidate.effectType === 'state_change' ||
      candidate.effectType === 'emote' ||
      candidate.effectType === 'navigate'
        ? candidate.effectType
        : 'text';
    const action = normalizeActionToken(candidate.action, `action_${index + 1}`);
    const label = sanitizeToken(candidate.label) || action.replace(/_/g, ' ');
    const description = sanitizeToken(candidate.description);
    if (!label) {
      return;
    }

    const interaction: AnnotationInteraction = {
      id:
        typeof candidate.id === 'string' && candidate.id
          ? candidate.id
          : `int-${crypto.randomUUID().slice(0, 8)}`,
      action,
      label,
      description,
      effectType,
      ...(normalizeInteractionPayload(effectType, candidate.effectPayload)
        ? { effectPayload: normalizeInteractionPayload(effectType, candidate.effectPayload) }
        : {}),
      ...(normalizeAnnotationStateRecord(candidate.requiredState)
        ? { requiredState: normalizeAnnotationStateRecord(candidate.requiredState) }
        : {}),
      ...(normalizeAnnotationStateRecord(candidate.stateChange)
        ? { stateChange: normalizeAnnotationStateRecord(candidate.stateChange) }
        : {}),
    };

    byAction.set(action, interaction);
  });

  return byAction.size > 0 ? [...byAction.values()] : undefined;
};

export const normalizeAnnotationTriggers = (value: unknown): AnnotationTrigger[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const byId = new Map<string, AnnotationTrigger>();
  value.slice(0, MAX_TRIGGERS).forEach((entry, index) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return;
    }
    const candidate = entry as Record<string, unknown>;
    const event = normalizeTriggerEvent(candidate.event);
    if (!event) {
      return;
    }
    const effect = normalizeTriggerEffect(event, candidate.effect);
    if (!effect) {
      return;
    }

    const id = sanitizeToken(candidate.id) || `trigger_${index + 1}`;
    const radius = Number(candidate.radius);
    const cooldownMs = Number(candidate.cooldownMs);
    const action =
      event === 'interact' ? normalizeActionToken(candidate.action, '') || undefined : undefined;
    const eventName = event === 'event' ? normalizeEventName(candidate.eventName) : undefined;
    const conditions =
      normalizeAnnotationConditionGroup(candidate.conditions) ??
      buildLegacyConditionGroup(candidate.condition);

    if (event === 'event' && !eventName) {
      return;
    }

    const trigger = {
      id,
      event,
      ...(action ? { action } : {}),
      ...(eventName ? { eventName } : {}),
      ...(event === 'proximity_enter' || event === 'proximity_exit'
        ? {
            radius: Number.isFinite(radius) ? Math.max(1, Math.floor(radius)) : 2,
          }
        : {}),
      ...(conditions ? { conditions } : {}),
      ...(Number.isFinite(cooldownMs)
        ? {
            cooldownMs: Math.max(0, Math.min(MAX_TRIGGER_COOLDOWN_MS, Math.floor(cooldownMs))),
          }
        : {}),
      effect,
    } as AnnotationTrigger; // REASON: event/eventName pairing validated earlier in this function
    byId.set(id, trigger);
  });

  return byId.size > 0 ? [...byId.values()] : undefined;
};

const buildImplicitTextInteraction = (
  annotation: Pick<
    WorldAnnotation,
    'category' | 'description' | 'examineText' | 'legacyType' | 'name'
  >,
): AnnotationInteraction | null => {
  const examineText = sanitizeToken(annotation.examineText);
  if (!examineText) {
    return null;
  }
  const isSign = annotation.category === 'sign' || annotation.legacyType === 'sign';
  return {
    action: 'examine',
    label: isSign ? `Read ${annotation.name}` : `Inspect ${annotation.name}`,
    description: isSign
      ? `Read the text on ${annotation.name}.`
      : annotation.description || `Inspect ${annotation.name}.`,
    effectType: 'text',
    effectPayload: examineText,
  };
};

const buildImplicitDoorInteraction = (
  annotation: Pick<WorldAnnotation, 'description' | 'doorTarget' | 'legacyType' | 'name'>,
): AnnotationInteraction | null => {
  if (!annotation.doorTarget) {
    return null;
  }

  return {
    action: 'enter',
    label: `Enter ${annotation.name}`,
    description: annotation.description || `Travel through ${annotation.name}.`,
    effectType: 'navigate',
    effectPayload: { ...annotation.doorTarget },
  };
};

export const getImplicitAnnotationInteractions = (
  annotation: Pick<
    WorldAnnotation,
    'category' | 'description' | 'doorTarget' | 'examineText' | 'legacyType' | 'name'
  >,
): AnnotationInteraction[] => {
  const interactions = [
    buildImplicitTextInteraction(annotation),
    buildImplicitDoorInteraction(annotation),
  ].filter((entry): entry is AnnotationInteraction => entry !== null);
  return interactions;
};

const statesMatch = (
  currentState: AnnotationStateRecord | undefined,
  requiredState: AnnotationStateRecord | undefined,
): boolean => {
  if (!requiredState) {
    return true;
  }
  return Object.entries(requiredState).every(([key, value]) => currentState?.[key] === value);
};

export const getAnnotationInteractions = (annotation: WorldAnnotation): AnnotationInteraction[] =>
  cloneAnnotationInteractions(annotation.interactions) ??
  getImplicitAnnotationInteractions(annotation);

export const getAvailableAnnotationInteractions = (
  annotation: WorldAnnotation,
): AnnotationInteraction[] =>
  getAnnotationInteractions(annotation).filter((interaction) =>
    statesMatch(annotation.state, interaction.requiredState),
  );

export const applyRuntimeStateToAnnotation = (
  annotation: WorldAnnotation,
  runtimeState: AnnotationStateRecord | undefined,
): WorldAnnotation => {
  const nextAnnotation: WorldAnnotation = {
    ...annotation,
    ...(cloneAnnotationTriggers(annotation.triggers)
      ? { triggers: cloneAnnotationTriggers(annotation.triggers) }
      : {}),
    ...(cloneAnnotationInteractions(annotation.interactions)
      ? { interactions: cloneAnnotationInteractions(annotation.interactions) }
      : {}),
    ...(cloneAnnotationStateRecord(annotation.state)
      ? { state: cloneAnnotationStateRecord(annotation.state) }
      : {}),
    ...(runtimeState
      ? {
          state: {
            ...(cloneAnnotationStateRecord(annotation.state) ?? {}),
            ...runtimeState,
          },
        }
      : {}),
  };

  Object.defineProperty(nextAnnotation, AUTHORED_STATE_SYMBOL, {
    value: cloneAnnotationStateRecord(annotation.state),
    enumerable: false,
    configurable: true,
  });

  return nextAnnotation;
};

const getAuthoredStateRecord = (annotation: WorldAnnotation): AnnotationStateRecord | undefined => {
  const runtimeAppliedAnnotation = annotation as WorldAnnotation & {
    [AUTHORED_STATE_SYMBOL]?: AnnotationStateRecord | undefined;
  };
  return cloneAnnotationStateRecord(
    runtimeAppliedAnnotation[AUTHORED_STATE_SYMBOL] ?? annotation.state,
  );
};

export const applyRuntimeStateToAnnotations = (
  annotations: readonly WorldAnnotation[],
  runtimeStateById: AnnotationRuntimeStateById | undefined,
): WorldAnnotation[] =>
  annotations.map((annotation) =>
    applyRuntimeStateToAnnotation(annotation, runtimeStateById?.[annotation.id]),
  );

export const stripRuntimeStateFromAnnotation = (annotation: WorldAnnotation): WorldAnnotation => {
  return {
    ...annotation,
    position: { ...annotation.position },
    ...(annotation.size ? { size: { ...annotation.size } } : {}),
    ...(annotation.tags ? { tags: [...annotation.tags] } : {}),
    ...(annotation.doorTarget ? { doorTarget: { ...annotation.doorTarget } } : {}),
    ...(cloneAnnotationTriggers(annotation.triggers)
      ? { triggers: cloneAnnotationTriggers(annotation.triggers) }
      : {}),
    ...(cloneAnnotationInteractions(annotation.interactions)
      ? { interactions: cloneAnnotationInteractions(annotation.interactions) }
      : {}),
    ...(getAuthoredStateRecord(annotation) ? { state: getAuthoredStateRecord(annotation) } : {}),
  };
};

export const stripRuntimeStateFromAnnotations = (
  annotations: readonly WorldAnnotation[] | null | undefined,
): WorldAnnotation[] =>
  Array.isArray(annotations) ? annotations.map(stripRuntimeStateFromAnnotation) : [];
